import React, { useState } from 'react';
import { toast } from 'sonner';
import smsService from '../../services/smsService';
import { X, Phone, CheckCircle, HourglassMedium, Envelope, WarningCircle } from '@phosphor-icons/react';
import './AttendanceAlertSMSModal.css';

function AttendanceAlertSMSModal({ isOpen, onClose, student, attendance, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  if (!isOpen || !student) return null;

  const handleSend = async () => {
    setLoading(true);
    try {
      const response = await smsService.sendAttendanceAlert({
        student_id: student.id,
        phone_number: student.parent_phone,
        attendance_percentage: parseFloat(attendance)
      });

      if (response.success) {
        toast.success(`Attendance alert sent to ${student.parent_name}!`);
        setConfirmed(true);
        setTimeout(() => {
          onClose();
          if (onSuccess) onSuccess();
          setConfirmed(false);
        }, 2000);
      }
    } catch (err) {
      toast.error('Failed to send SMS: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const messageSample = `Alert: ${student.first_name}'s attendance is ${attendance}%. Please monitor. -School`;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal attendance-sms-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            <Phone size={24} />
            Send Attendance Alert SMS
          </h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        {confirmed ? (
          <div className="modal-body success-state">
            <div className="success-icon"><CheckCircle size={24} /></div>
            <h3>SMS Sent Successfully!</h3>
            <p>Attendance alert sent to {student.parent_name}</p>
          </div>
        ) : (
          <>
            <div className="modal-body">
              {/* Student Info */}
              <div className="info-section">
                <h3>Student Information</h3>
                <div className="info-row">
                  <span className="info-label">Student:</span>
                  <span className="info-value">{student.first_name} {student.last_name}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Parent Name:</span>
                  <span className="info-value">{student.parent_name}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Parent Phone:</span>
                  <span className="info-value phone">{student.parent_phone || 'Not available'}</span>
                </div>
              </div>

              {/* Attendance Details */}
              <div className="info-section">
                <h3>Attendance Details</h3>
                <div className="info-row">
                  <span className="info-label">Current Attendance:</span>
                  <span className={`info-value attendance ${
                    parseFloat(attendance) < 75 ? 'critical' : 'good'
                  }`}>
                    {attendance}%
                  </span>
                </div>
                {parseFloat(attendance) < 75 && (
                  <div className="warning-box">
                    <WarningCircle size={18} /> Attendance is below 75% threshold
                  </div>
                )}
              </div>

              {/* Message Preview */}
              <div className="message-section">
                <h3><Envelope size={18} /> SMS Message Preview</h3>
                <div className="message-box">
                  <p>{messageSample}</p>
                </div>
                <p className="message-hint">This is how the SMS will appear to the parent</p>
              </div>
            </div>

            <div className="modal-footer">
              <button
                onClick={onClose}
                className="btn btn-secondary"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                onClick={handleSend}
                className="btn btn-primary"
                disabled={loading || !student.parent_phone}
              >
                {loading ? (<><HourglassMedium size={18} /> Sending...</>) : (<><CheckCircle size={18} /> Send SMS</>)}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default AttendanceAlertSMSModal;
